import { zodResolver } from "@hookform/resolvers/zod";
import { getAccessProfileQueryOptions } from "@packages/api-client/hooks/authorizationController";
import { getMyOrganizationsQueryOptions } from "@packages/api-client/hooks/organizationController";
import type { GetMyOrganizations200 } from "@packages/api-client/types/organizationController";
import { PlatformSubscriptionStatus, Role } from "@packages/shared-types";
import { useQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	ArrowLeft,
	ArrowRight,
	Building2,
	Check,
	Info,
	Mail,
	ShieldCheck,
	Trash2,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	COUNTRY_CODES,
	countryFieldSchema,
	countryLabel,
	DEFAULT_COUNTRY_CODE,
	useCreateOrganization,
	useDeleteOrganization,
} from "@/api/organizations";
import {
	type MyInvitation,
	myInvitationsQueryOptions,
	useAcceptInvitationById,
	useDeclineInvitation,
} from "@/api/staff";
import { Badge } from "@/components/badge";
import { ConfirmDestructiveDialog } from "@/components/ConfirmDestructiveDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	clearStoredAttribution,
	readStoredAttribution,
} from "@/lib/attribution";
import { rememberInvitedMemberWelcome } from "@/lib/onboarding/invitedMemberWelcome";
import { requiredString } from "@/utils/formSchemas";
import { getOrganizationRoleLabel } from "@/utils/roleUtils";
import { cn } from "@/utils/utils";
import { AppBaseLayout } from "./_app/-components/AppBaseLayout";

const EXPLAINER_OPEN_KEY = "ruralops:org-picker-explainer-open";

function useOrgExplainer() {
	const [isOpen, setIsOpen] = useState<boolean>(() => {
		if (typeof window === "undefined") return false;
		return window.localStorage.getItem(EXPLAINER_OPEN_KEY) === "1";
	});

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (isOpen) window.localStorage.setItem(EXPLAINER_OPEN_KEY, "1");
		else window.localStorage.removeItem(EXPLAINER_OPEN_KEY);
	}, [isOpen]);

	return {
		isOpen,
		open: () => setIsOpen(true),
		close: () => setIsOpen(false),
	};
}

/**
 * The picker is the one place the organization list must be current: it is
 * where memberships are gained (accept), dropped (delete/decline) and, in
 * dev, where a reseeded database first shows. The app default (60s stale on
 * top of the persisted cache) would serve the previous list across a reload,
 * so this page always revalidates. The org route keeps its own
 * `staleTime: Infinity` — that one is the offline app-shell contract.
 */
const pickerOrganizationsQueryOptions = () => ({
	...getMyOrganizationsQueryOptions(),
	staleTime: 0,
});

export const Route = createFileRoute("/_auth/app/select-organization")({
	loader: async ({ context }) => {
		// `revalidateIfStale`: hand back the cached list at once (offline-first
		// still renders) while the fresh read is already in flight.
		context.queryClient.ensureQueryData({
			...pickerOrganizationsQueryOptions(),
			revalidateIfStale: true,
		});
		context.queryClient.ensureQueryData({
			...myInvitationsQueryOptions(),
			revalidateIfStale: true,
		});
	},
	component: SelectOrgPage,
});

type OrgListItem = GetMyOrganizations200[number];

type OrgStatus = {
	status: PlatformSubscriptionStatus | null;
	planSlug: string | null;
	trialEndsAt: string | null;
	isLoading: boolean;
	isError: boolean;
};

function useOrgAccessProfiles(orgs: OrgListItem[]): Record<string, OrgStatus> {
	const results = useQueries({
		queries: orgs.map((org) => ({
			...getAccessProfileQueryOptions({ organizationId: org.id }),
			staleTime: 60_000,
			retry: 1,
		})),
	});

	return useMemo(() => {
		const map: Record<string, OrgStatus> = {};
		orgs.forEach((org, i) => {
			const r = results[i];
			map[org.id] = {
				status: (r?.data?.subscriptionStatus ??
					null) as PlatformSubscriptionStatus | null,
				planSlug: r?.data?.planSlug ?? null,
				trialEndsAt: r?.data?.trialEndsAt ?? null,
				isLoading: r?.isPending ?? true,
				isError: r?.isError ?? false,
			};
		});
		return map;
	}, [orgs, results]);
}

type StatusKind =
	| "checking"
	| "active"
	| "trialing"
	| "setup_required"
	| "payment_required"
	| "past_due"
	| "inactive"
	| "awaiting_owner"
	| "unknown";

function deriveStatusKind(profile: OrgStatus, isOwner: boolean): StatusKind {
	if (profile.isLoading) return "checking";
	if (profile.isError || profile.status === null)
		return isOwner ? "setup_required" : "unknown";
	switch (profile.status) {
		case PlatformSubscriptionStatus.ACTIVE:
			return "active";
		case PlatformSubscriptionStatus.TRIALING:
			return "trialing";
		case PlatformSubscriptionStatus.PAST_DUE:
			return isOwner ? "past_due" : "awaiting_owner";
		case PlatformSubscriptionStatus.UNPAID:
			return isOwner ? "payment_required" : "awaiting_owner";
		case PlatformSubscriptionStatus.INCOMPLETE:
			return isOwner ? "setup_required" : "awaiting_owner";
		case PlatformSubscriptionStatus.INCOMPLETE_EXPIRED:
		case PlatformSubscriptionStatus.CANCELED:
		case PlatformSubscriptionStatus.PAUSED:
			return isOwner ? "inactive" : "awaiting_owner";
		default:
			return "unknown";
	}
}

function trialDaysLeft(trialEndsAt: string | null): number | null {
	if (!trialEndsAt) return null;
	const end = new Date(trialEndsAt).getTime();
	if (Number.isNaN(end)) return null;
	const days = Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24));
	return days > 0 ? days : null;
}

function planLabelKey(planSlug: string | null): string | null {
	if (!planSlug) return null;
	const s = planSlug.toLowerCase();
	if (s.includes("enterprise")) return "selectOrganization.plan.enterprise";
	if (s.includes("professional")) return "selectOrganization.plan.professional";
	if (s.includes("starter")) return "selectOrganization.plan.starter";
	if (s.includes("free")) return "selectOrganization.plan.free";
	return null;
}

function SelectOrgPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: organizations } = useSuspenseQuery(
		pickerOrganizationsQueryOptions(),
	);
	const { data: invitations } = useSuspenseQuery(myInvitationsQueryOptions());

	const ownedOrgs = useMemo(
		() => organizations.filter((o) => o.role === Role.ORG_OWNER),
		[organizations],
	);
	const invitedOrgs = useMemo(
		() => organizations.filter((o) => o.role !== Role.ORG_OWNER),
		[organizations],
	);

	const profiles = useOrgAccessProfiles(organizations);

	// Create Organization Dialog state
	// Two-step flow: "explainer" → "form" → null (closed)
	const [createStep, setCreateStep] = useState<"explainer" | "form" | null>(
		null,
	);
	const createOrganizationMutation = useCreateOrganization();
	const deleteOrganizationMutation = useDeleteOrganization();
	const [orgToDelete, setOrgToDelete] = useState<OrgListItem | null>(null);

	// This is an account/organization-management surface: it never teaches the
	// product. Creating an organization closes the dialog and hands straight off
	// to account setup (plan/trial); product onboarding starts later, inside
	// `/app/org/:orgId`. See docs/Plans/2026-08-13-ruralops-organization-onboarding.md §3.1.
	const handleCreateOrganization = (values: CreateOrgFormValues) =>
		createOrganizationMutation
			.mutateAsync({
				data: {
					name: values.name.trim(),
					description: values.description.trim() || undefined,
					defaultCurrency: values.currency,
					country: values.country,
					// The bundle the public site's acquisition link carried across
					// Clerk. `undefined` for unattributed traffic — the field is
					// optional and no row is written for it.
					attribution: readStoredAttribution(),
				},
			})
			.then((result) => {
				// Cleared only on success, and only here: the bundle describes the
				// arrival that produced *this* organization, so it must not survive
				// to attach itself to the next one. A failed attempt keeps it, so a
				// retry is still attributed.
				clearStoredAttribution();
				setCreateStep(null);
				navigate({
					to: "/app/org/renew-platform-plan",
					search: { orgId: result.id },
				});
			});

	const handleCloseCreateFlow = () => {
		setCreateStep(null);
	};

	const handleOpenDeleteDialog = (org: OrgListItem) => {
		setOrgToDelete(org);
	};

	const handleCloseDeleteDialog = () => {
		setOrgToDelete(null);
	};

	const handleConfirmDeleteOrganization = async () => {
		if (!orgToDelete) return;
		try {
			await deleteOrganizationMutation.mutateAsync({
				organizationId: orgToDelete.id,
			});
			setOrgToDelete(null);
		} catch (error) {
			console.error("Failed to delete organization:", error);
		}
	};

	// A pending invitation counts: someone invited only, with nothing of their
	// own yet, must see the invite rather than the "create your first" pitch.
	const hasAnyOrgs = organizations.length > 0 || invitations.length > 0;
	const explainer = useOrgExplainer();

	return (
		<AppBaseLayout showMobileHeader>
			<div className="h-full overflow-y-auto bg-base-100">
				<div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 py-6 sm:py-10 px-3 sm:px-4">
					<PageHeader
						hasAnyOrgs={hasAnyOrgs}
						onCreate={() => setCreateStep("explainer")}
						showExplainerTrigger={hasAnyOrgs && !explainer.isOpen}
						onOpenExplainer={explainer.open}
					/>

					{hasAnyOrgs && explainer.isOpen && (
						<OrgExplainerPanel onClose={explainer.close} />
					)}

					<CreateOrganizationFlow
						step={createStep}
						onStepChange={setCreateStep}
						onClose={handleCloseCreateFlow}
						onSubmit={handleCreateOrganization}
						isSubmitting={createOrganizationMutation.isPending}
					/>

					{!hasAnyOrgs ? (
						<EmptyStateAll onCreate={() => setCreateStep("explainer")} />
					) : (
						<div className="space-y-8 sm:space-y-10">
							<Section
								title={t("selectOrganization.ownedTitle")}
								items={ownedOrgs}
								profiles={profiles}
								testId="owned-organizations-section"
								onRequestDelete={handleOpenDeleteDialog}
								ownerControls
								emptyText={t("selectOrganization.emptyOwned")}
							/>
							<Section
								title={t("selectOrganization.invitedTitle")}
								items={invitedOrgs}
								invitations={invitations}
								profiles={profiles}
								testId="invited-organizations-section"
								ownerControls={false}
								emptyText={t("selectOrganization.emptyInvited")}
							/>
						</div>
					)}
				</div>
			</div>

			{orgToDelete && (
				<ConfirmDestructiveDialog
					open
					onOpenChange={(open) => {
						if (!open) handleCloseDeleteDialog();
					}}
					title={t("selectOrganization.deleteDialog.title")}
					description={t("selectOrganization.deleteDialog.description", {
						organizationName: orgToDelete.name,
					})}
					confirmationValue={orgToDelete.name}
					confirmLabel={t("selectOrganization.deleteDialog.confirmButton")}
					pending={deleteOrganizationMutation.isPending}
					onConfirm={handleConfirmDeleteOrganization}
				>
					<div className="space-y-3 pt-2">
						<p className="text-sm font-semibold text-error">
							{t("selectOrganization.deleteDialog.irreversibleWarning")}
						</p>
						<div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning-content">
							{t("selectOrganization.deleteDialog.dataLossWarning")}
						</div>
					</div>
				</ConfirmDestructiveDialog>
			)}
		</AppBaseLayout>
	);
}

function PageHeader({
	hasAnyOrgs,
	onCreate,
	showExplainerTrigger,
	onOpenExplainer,
}: {
	hasAnyOrgs: boolean;
	onCreate: () => void;
	showExplainerTrigger: boolean;
	onOpenExplainer: () => void;
}) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
			<div className="space-y-1 sm:space-y-2 max-w-2xl">
				<h1
					data-testid="page-title"
					className="text-3xl font-bold text-base-content tracking-tight"
				>
					{t("selectOrganization.title")}
				</h1>
				<p className="text-sm sm:text-base text-base-content/70">
					{t("selectOrganization.subtitle")}
				</p>
				{showExplainerTrigger && (
					<button
						type="button"
						onClick={onOpenExplainer}
						data-testid="org-explainer-trigger"
						className="mt-1 inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors underline decoration-dotted underline-offset-4 decoration-primary/40 hover:decoration-primary/70"
					>
						<Info className="h-3.5 w-3.5" />
						{t("selectOrganization.explainer.title")}
					</button>
				)}
			</div>
			{hasAnyOrgs && (
				<Button
					className="btn btn-primary text-primary-content w-full sm:w-auto shrink-0"
					onClick={onCreate}
				>
					{t("organization.createNew")}
				</Button>
			)}
		</div>
	);
}

function OrgExplainerPanel({ onClose }: { onClose: () => void }) {
	const { t } = useTranslation();
	return (
		<aside
			data-testid="org-explainer"
			className="relative rounded-xl border border-base-300 bg-base-200/60 p-4 sm:p-5"
		>
			<div className="flex items-start gap-3 sm:gap-4 pr-8">
				<div className="mt-0.5 shrink-0 rounded-lg bg-primary/10 p-2 text-primary">
					<Info className="h-4 w-4 sm:h-5 sm:w-5" />
				</div>
				<div className="min-w-0 flex-1 space-y-2">
					<h2 className="text-sm sm:text-base font-semibold text-base-content">
						{t("selectOrganization.explainer.title")}
					</h2>
					<div className="space-y-2 text-sm text-base-content/80 leading-relaxed">
						<p>{t("selectOrganization.explainer.definition")}</p>
						<p>{t("selectOrganization.explainer.rule")}</p>
						<p>{t("selectOrganization.explainer.newOrg")}</p>
					</div>
				</div>
			</div>
			<button
				type="button"
				onClick={onClose}
				aria-label={t("selectOrganization.explainer.hide")}
				className="absolute top-3 right-3 rounded-md p-1 text-base-content/50 hover:bg-base-300 hover:text-base-content transition-colors"
			>
				<X className="h-4 w-4" />
			</button>
		</aside>
	);
}

function EmptyStateAll({ onCreate }: { onCreate: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="mt-2 sm:mt-4 rounded-2xl border border-base-300 bg-base-200/40 p-6 sm:p-10 text-center space-y-4">
			<div className="mx-auto w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
				<Building2 className="h-7 w-7" />
			</div>
			<div className="space-y-1.5 max-w-md mx-auto">
				<h3 className="text-lg sm:text-xl font-semibold text-base-content">
					{t("organization.noOrganizations")}
				</h3>
				<p className="text-sm sm:text-base text-base-content/70">
					{t("organization.noOrganizationsDescription")}
				</p>
			</div>
			<div className="pt-2">
				<Button
					className="btn btn-primary text-primary-content"
					onClick={onCreate}
				>
					{t("organization.createFirst")}
				</Button>
			</div>
		</div>
	);
}

function Section({
	title,
	items,
	invitations = [],
	profiles,
	testId,
	onRequestDelete,
	ownerControls,
	emptyText,
}: {
	title: string;
	items: OrgListItem[];
	/** Pending invitations, listed ahead of the memberships they may become. */
	invitations?: MyInvitation[];
	profiles: Record<string, OrgStatus>;
	testId?: string;
	onRequestDelete?: (org: OrgListItem) => void;
	ownerControls: boolean;
	emptyText?: string;
}) {
	const { t } = useTranslation();
	const count = invitations.length + items.length;

	return (
		<section className="space-y-3 sm:space-y-4" data-testid={testId}>
			<header className="flex items-center gap-2">
				<h2 className="text-base sm:text-lg font-semibold text-base-content flex items-center gap-2">
					{ownerControls ? (
						<ShieldCheck className="h-4 w-4 text-primary" />
					) : (
						<Mail className="h-4 w-4 text-accent" />
					)}
					{title}
				</h2>
				<span className="text-xs sm:text-sm text-base-content/50" aria-hidden>
					·
				</span>
				<span className="text-xs sm:text-sm text-base-content/60">
					{t("selectOrganization.countLabel", { count })}
				</span>
			</header>

			{count === 0 ? (
				<div className="rounded-xl border border-dashed border-base-300 bg-base-200/30 p-4 sm:p-6 text-center text-sm text-base-content/70">
					{emptyText}
				</div>
			) : (
				<div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
					{invitations.map((invitation) => (
						<InvitationCard key={invitation.id} invitation={invitation} />
					))}
					{items.map((org) => (
						<OrgCard
							key={org.id}
							org={org}
							profile={
								profiles[org.id] ?? {
									status: null,
									planSlug: null,
									trialEndsAt: null,
									isLoading: true,
									isError: false,
								}
							}
							onRequestDelete={
								ownerControls && onRequestDelete
									? () => onRequestDelete(org)
									: undefined
							}
						/>
					))}
				</div>
			)}
		</section>
	);
}

function OrgCard({
	org,
	profile,
	onRequestDelete,
}: {
	org: OrgListItem;
	profile: OrgStatus;
	onRequestDelete?: () => void;
}) {
	const { t } = useTranslation();
	const isOwner = org.role === Role.ORG_OWNER;
	const kind = deriveStatusKind(profile, isOwner);

	// Route based on status intent so the primary CTA lands users where they need to be.
	const goesToRenew =
		isOwner &&
		(kind === "setup_required" ||
			kind === "payment_required" ||
			kind === "past_due" ||
			kind === "inactive");

	const isDisabled = kind === "awaiting_owner";

	const actionLabel =
		kind === "setup_required"
			? t("selectOrganization.action.choosePlan")
			: kind === "payment_required" || kind === "past_due"
				? t("selectOrganization.action.updatePayment")
				: kind === "inactive"
					? t("selectOrganization.action.reactivate")
					: kind === "awaiting_owner"
						? t("selectOrganization.action.awaitingOwner")
						: t("selectOrganization.action.open");

	const CardShell = (
		<Card
			data-testid="organization-card"
			className={cn(
				"group h-full flex flex-col overflow-hidden border border-base-300 bg-base-100 transition-all duration-200",
				isDisabled
					? "opacity-70"
					: "hover:border-primary/60 hover:shadow-md hover:-translate-y-0.5",
			)}
		>
			<CardContent className="flex-1 p-4 sm:p-5 flex flex-col gap-3.5">
				<div className="flex items-start justify-between gap-2">
					<div className="flex items-center gap-2.5 min-w-0">
						<span className="shrink-0 rounded-lg bg-primary/10 p-2 text-primary">
							<Building2 className="h-4 w-4" />
						</span>
						<div className="min-w-0">
							<h3
								data-testid="organization-name"
								className="text-base sm:text-[17px] font-semibold text-base-content leading-snug line-clamp-2 break-words"
							>
								{org.name}
							</h3>
							<p
								data-testid="organization-role"
								className="text-xs text-base-content/60 mt-0.5"
							>
								{getOrganizationRoleLabel(org.role as Role, t)}
							</p>
						</div>
					</div>
					{onRequestDelete && (
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										onRequestDelete();
									}}
									data-testid="delete-organization-button"
									aria-label={t("selectOrganization.deleteButton")}
									className="shrink-0 rounded-md p-1.5 text-base-content/40 hover:bg-error/10 hover:text-error transition-colors opacity-70 hover:opacity-100"
								>
									<Trash2 className="h-4 w-4" />
								</button>
							</TooltipTrigger>
							<TooltipContent>
								{t("selectOrganization.deleteButton")}
							</TooltipContent>
						</Tooltip>
					)}
				</div>

				{org.description && (
					<p className="text-xs sm:text-sm text-base-content/60 line-clamp-2 break-words">
						{org.description}
					</p>
				)}

				<div className="flex-1" />

				<StatusRow kind={kind} profile={profile} />
			</CardContent>

			<div className="border-t border-base-300 bg-base-200/40 p-3 sm:p-4">
				<Button
					data-testid="select-organization-button"
					disabled={isDisabled}
					className={cn(
						"w-full justify-center gap-2 btn text-primary-content",
						isDisabled ? "btn-disabled" : "btn-primary",
					)}
				>
					{actionLabel}
					{!isDisabled && (
						<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
					)}
				</Button>
			</div>
		</Card>
	);

	if (isDisabled) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<div>{CardShell}</div>
				</TooltipTrigger>
				<TooltipContent>{t("selectOrganization.invitedInfo")}</TooltipContent>
			</Tooltip>
		);
	}

	return (
		<Link
			to={goesToRenew ? "/app/org/renew-platform-plan" : "/app/org/$orgId"}
			params={goesToRenew ? undefined : { orgId: org.id }}
			search={goesToRenew ? { orgId: org.id } : undefined}
			className="cursor-pointer block h-full"
		>
			{CardShell}
		</Link>
	);
}

function InvitationCard({ invitation }: { invitation: MyInvitation }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const acceptMutation = useAcceptInvitationById();
	const declineMutation = useDeclineInvitation();
	const isBusy = acceptMutation.isPending || declineMutation.isPending;

	// Same landing as the e-mail link's accept page: straight into the
	// organization, with the inviter's name carried along for the first view.
	//
	// `mutateAsync`, not `mutate(vars, { onSuccess })`: the hook's own
	// onSuccess refreshes the organization list, which swaps this card for a
	// membership card — and a per-call `onSuccess` is dropped once the
	// component that issued it has unmounted. The promise is not.
	const handleAccept = useCallback(() => {
		acceptMutation
			.mutateAsync({ invitationId: invitation.id })
			.then((result) => {
				if (!result?.organizationId) return;
				rememberInvitedMemberWelcome(
					result.organizationId,
					invitation.inviterName,
				);
				navigate({
					to: "/app/org/$orgId",
					params: { orgId: result.organizationId },
				});
			})
			// The mutation cache already toasts the failure; the card stays.
			.catch(() => undefined);
	}, [acceptMutation, invitation.id, invitation.inviterName, navigate]);

	const handleDecline = useCallback(() => {
		declineMutation.mutate({ invitationId: invitation.id });
	}, [declineMutation, invitation.id]);

	return (
		<Card
			data-testid="invitation-card"
			className="group h-full flex flex-col overflow-hidden border border-accent/40 bg-base-100 transition-all duration-200"
		>
			<CardContent className="flex-1 p-4 sm:p-5 flex flex-col gap-3.5">
				<div className="flex items-start justify-between gap-2">
					<div className="flex items-center gap-2.5 min-w-0">
						<span className="shrink-0 rounded-lg bg-accent/10 p-2 text-accent">
							<Mail className="h-4 w-4" />
						</span>
						<div className="min-w-0">
							<h3
								data-testid="invitation-organization-name"
								className="text-base sm:text-[17px] font-semibold text-base-content leading-snug line-clamp-2 break-words"
							>
								{invitation.organizationName}
							</h3>
							<p
								data-testid="invitation-role"
								className="text-xs text-base-content/60 mt-0.5"
							>
								{getOrganizationRoleLabel(invitation.role as Role, t)}
							</p>
						</div>
					</div>
					<Badge
						variant="outline"
						className="border-accent/40 bg-accent/10 text-accent text-[10px] sm:text-[11px] font-medium px-2 py-0 shrink-0"
					>
						{t("selectOrganization.invitation.pendingBadge")}
					</Badge>
				</div>

				<p className="text-xs sm:text-sm text-base-content/60 break-words">
					{t("selectOrganization.invitation.invitedBy", {
						inviterName: invitation.inviterName,
					})}
				</p>

				<div className="flex-1" />
			</CardContent>

			<div className="border-t border-base-300 bg-base-200/40 p-3 sm:p-4 flex gap-2">
				<Button
					type="button"
					variant="outline"
					data-testid="decline-invitation-button"
					disabled={isBusy}
					onClick={handleDecline}
					className="flex-1 justify-center gap-1.5"
				>
					<X className="h-4 w-4" />
					{t("selectOrganization.invitation.decline")}
				</Button>
				<Button
					type="button"
					data-testid="accept-invitation-button"
					disabled={isBusy}
					onClick={handleAccept}
					className="flex-1 justify-center gap-1.5 btn btn-primary text-primary-content"
				>
					<Check className="h-4 w-4" />
					{t("selectOrganization.invitation.accept")}
				</Button>
			</div>
		</Card>
	);
}

function StatusRow({
	kind,
	profile,
}: {
	kind: StatusKind;
	profile: OrgStatus;
}) {
	const { t } = useTranslation();

	if (kind === "checking") {
		return (
			<div className="flex items-center gap-2 text-xs text-base-content/50">
				<span className="h-1.5 w-1.5 rounded-full bg-base-300 animate-pulse" />
				{t("selectOrganization.status.checking")}
			</div>
		);
	}

	const trialDays =
		kind === "trialing" ? trialDaysLeft(profile.trialEndsAt) : null;

	const planKey = planLabelKey(profile.planSlug);

	const config: Record<
		Exclude<StatusKind, "checking">,
		{ dot: string; text: string; label: string }
	> = {
		active: {
			dot: "bg-success",
			text: "text-success",
			label: t("selectOrganization.status.active"),
		},
		trialing: {
			dot: "bg-accent",
			text: "text-accent",
			label:
				trialDays !== null
					? t("selectOrganization.status.trialingWithDays", {
							count: trialDays,
						})
					: t("selectOrganization.status.trialing"),
		},
		setup_required: {
			dot: "bg-warning",
			text: "text-warning",
			label: t("selectOrganization.status.setupRequired"),
		},
		payment_required: {
			dot: "bg-error",
			text: "text-error",
			label: t("selectOrganization.status.paymentRequired"),
		},
		past_due: {
			dot: "bg-error",
			text: "text-error",
			label: t("selectOrganization.status.pastDue"),
		},
		inactive: {
			dot: "bg-base-content/40",
			text: "text-base-content/60",
			label: t("selectOrganization.status.inactive"),
		},
		awaiting_owner: {
			dot: "bg-base-content/30",
			text: "text-base-content/60",
			label: t("selectOrganization.status.awaitingOwner"),
		},
		unknown: {
			dot: "bg-base-content/30",
			text: "text-base-content/50",
			label: t("selectOrganization.status.unknown"),
		},
	};

	const { dot, text, label } = config[kind];

	return (
		<div className="flex items-center gap-2 min-w-0">
			<span className={cn("h-2 w-2 rounded-full shrink-0", dot)} aria-hidden />
			<span className={cn("text-xs sm:text-sm font-medium truncate", text)}>
				{label}
			</span>
			{planKey && (kind === "active" || kind === "trialing") && (
				<>
					<span className="text-base-content/30 text-xs shrink-0" aria-hidden>
						·
					</span>
					<Badge
						variant="outline"
						className="border-base-300 bg-base-200/60 text-[10px] sm:text-[11px] font-medium px-2 py-0 shrink-0"
					>
						{t(planKey)}
					</Badge>
				</>
			)}
		</div>
	);
}

type CreateStep = "explainer" | "form" | null;

const CREATE_ORG_FORM_ID = "select-org-create-form";

const createOrgSchema = z.object({
	name: requiredString(),
	description: z.string(),
	currency: z.enum(["BRL", "USD"]),
	country: countryFieldSchema,
});
type CreateOrgFormValues = z.infer<typeof createOrgSchema>;

const EMPTY_CREATE_ORG: CreateOrgFormValues = {
	name: "",
	description: "",
	currency: "BRL",
	country: DEFAULT_COUNTRY_CODE,
};

function CreateOrganizationFlow({
	step,
	onStepChange,
	onClose,
	onSubmit,
	isSubmitting,
}: {
	step: CreateStep;
	onStepChange: (step: CreateStep) => void;
	onClose: () => void;
	onSubmit: (values: CreateOrgFormValues) => Promise<unknown>;
	isSubmitting: boolean;
}) {
	const { t } = useTranslation();

	const form = useForm<CreateOrgFormValues>({
		resolver: zodResolver(createOrgSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: EMPTY_CREATE_ORG,
	});

	// Reset each time the flow (re)opens at its entry step.
	useEffect(() => {
		if (step === "explainer") form.reset(EMPTY_CREATE_ORG);
	}, [step, form]);

	const guardedSubmit = useGuardedSubmit(form, onSubmit);

	const isExplainer = step === "explainer";
	const isForm = step === "form";

	return (
		<ResponsiveFormDialog
			open={step !== null}
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
			pending={isSubmitting}
			// 768px: the explainer step is six paragraphs of prose and the form
			// step four fields with help text — both read cramped at the 512px
			// default, which forced the body into its own scroll.
			size="xl"
			title={
				isExplainer
					? t("organization.createExplainer.title")
					: t("organization.createNew")
			}
			description={isForm ? t("organization.createDescription") : undefined}
			footer={
				isExplainer ? (
					<>
						<Button type="button" variant="outline" onClick={onClose}>
							{t("common.cancel")}
						</Button>
						<Button
							type="button"
							className="gap-2"
							onClick={() => onStepChange("form")}
							data-testid="create-org-continue-button"
						>
							{t("common.continue")}
							<ArrowRight className="h-4 w-4" />
						</Button>
					</>
				) : (
					<>
						<Button
							type="button"
							variant="ghost"
							className="gap-2"
							onClick={() => onStepChange("explainer")}
							disabled={isSubmitting}
							data-testid="create-org-back-button"
						>
							<ArrowLeft className="h-4 w-4" />
							{t("common.back")}
						</Button>
						<SubmitButton
							form={CREATE_ORG_FORM_ID}
							pending={isSubmitting}
							pendingLabel={t("common.creating")}
						>
							{t("common.create")}
						</SubmitButton>
					</>
				)
			}
		>
			<StepProgress currentStep={isExplainer ? 1 : 2} totalSteps={2} />

			{isExplainer ? (
				<ExplainerStepContent />
			) : (
				<Form {...form}>
					<form id={CREATE_ORG_FORM_ID} onSubmit={guardedSubmit}>
						<FormStepContent />
						<FormRootMessage className="mt-4" />
					</form>
				</Form>
			)}
		</ResponsiveFormDialog>
	);
}

function StepProgress({
	currentStep,
	totalSteps,
}: {
	currentStep: number;
	totalSteps: number;
}) {
	const { t } = useTranslation();
	return (
		<div className="flex items-center gap-3 pt-1 pb-4 border-b border-base-300 mb-5">
			<div className="flex items-center gap-1.5">
				{Array.from({ length: totalSteps }).map((_, i) => {
					const stepNumber = i + 1;
					const isActive = stepNumber === currentStep;
					const isDone = stepNumber < currentStep;
					return (
						<span
							key={`step-${stepNumber}`}
							className={cn(
								"h-1.5 rounded-full transition-all duration-300",
								isActive
									? "w-8 bg-primary"
									: isDone
										? "w-4 bg-primary/60"
										: "w-4 bg-base-300",
							)}
						/>
					);
				})}
			</div>
			<span className="text-xs text-base-content/60">
				{t("onboarding.stepOf", { current: currentStep, total: totalSteps })}
			</span>
		</div>
	);
}

function ExplainerStepContent() {
	const { t } = useTranslation();
	return (
		<div className="flex items-start gap-3">
			<div className="shrink-0 rounded-xl bg-primary/10 p-3 text-primary">
				<Building2 className="h-5 w-5 sm:h-6 sm:w-6" />
			</div>
			<div className="min-w-0 flex-1 space-y-3 pt-1 text-sm sm:text-base text-base-content/80 leading-relaxed">
				<p>{t("organization.createExplainer.definition")}</p>
				<p>{t("organization.createExplainer.contents")}</p>
				<p>{t("organization.createExplainer.rule")}</p>
				<p>{t("organization.createExplainer.billing")}</p>
				<p>{t("organization.createExplainer.newOrgWhen")}</p>
				<p>{t("organization.createExplainer.newOrgWhenNot")}</p>
			</div>
		</div>
	);
}

function FormStepContent() {
	const { t } = useTranslation();
	const { control } = useFormContext<CreateOrgFormValues>();

	return (
		<div className="grid gap-5">
			<FormField
				control={control}
				name="name"
				render={({ field }) => (
					<FormItem>
						<FormLabel required>{t("organization.name")}</FormLabel>
						<FormControl>
							<Input
								{...field}
								placeholder={t("organization.namePlaceholder")}
								maxLength={100}
								autoFocus
							/>
						</FormControl>
						<FormDescription>{t("organization.nameHelp")}</FormDescription>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={control}
				name="description"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("organization.description")}</FormLabel>
						<FormControl>
							<Textarea
								{...field}
								placeholder={t("organization.descriptionPlaceholder")}
								maxLength={500}
								rows={3}
								className="min-h-20 resize-none"
							/>
						</FormControl>
						<FormDescription>
							{t("organization.descriptionHelp")}
						</FormDescription>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={control}
				name="country"
				render={({ field }) => (
					<FormItem>
						<FormLabel required>{t("organization.country.label")}</FormLabel>
						<Select value={field.value} onValueChange={field.onChange}>
							<FormControl>
								<SelectTrigger data-testid="create-org-country-trigger">
									<SelectValue
										placeholder={t("organization.country.placeholder")}
									/>
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{COUNTRY_CODES.map((code) => (
									<SelectItem key={code} value={code}>
										{countryLabel(t, code)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<FormDescription>{t("organization.country.help")}</FormDescription>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={control}
				name="currency"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("organization.currency.label")}</FormLabel>
						<Select value={field.value} onValueChange={field.onChange}>
							<FormControl>
								<SelectTrigger>
									<SelectValue
										placeholder={t("organization.currency.placeholder")}
									/>
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								<SelectItem value="BRL">
									{t("organization.currency.brl")}
								</SelectItem>
								<SelectItem value="USD">
									{t("organization.currency.usd")}
								</SelectItem>
							</SelectContent>
						</Select>
						<div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs sm:text-sm text-warning-content">
							<Info className="mt-0.5 h-4 w-4 shrink-0" />
							<span>{t("organization.currency.warning")}</span>
						</div>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	);
}
